import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { CLICK_COMMAND, COMMAND_PRIORITY_LOW, DecoratorNode } from "lexical";
import type { ReactElement } from "react";
import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

export type VideoPlatform = "youtube" | "vimeo";

export type SerializedVideoEmbedNode = Spread<
  {
    videoUrl: string;
    videoId: string;
    platform: VideoPlatform;
  },
  SerializedLexicalNode
>;

function VideoEmbedComponent({
  videoId,
  platform,
  nodeKey,
}: {
  videoId: string;
  platform: VideoPlatform;
  nodeKey: NodeKey;
}) {
  const [editor] = useLexicalComposerContext();
  const ref = useRef<HTMLDivElement>(null);
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);

  useEffect(() => {
    return editor.registerCommand(
      CLICK_COMMAND,
      (event: MouseEvent) => {
        if (ref.current?.contains(event.target as Node)) {
          clearSelection();
          setSelected(true);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, clearSelection, setSelected]);

  const src =
    platform === "youtube"
      ? `https://www.youtube.com/embed/${videoId}`
      : `https://player.vimeo.com/video/${videoId}`;

  return (
    <div
      ref={ref}
      className={twMerge(
        "relative w-full my-4 rounded-lg",
        isSelected && "ring-2 ring-blue-500",
      )}
      style={{ paddingBottom: "56.25%" }}
    >
      <iframe
        className="pointer-events-none absolute inset-0 h-full w-full rounded-lg"
        src={src}
        title="Video embed"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export class VideoEmbedNode extends DecoratorNode<ReactElement> {
  __videoUrl: string;
  __videoId: string;
  __platform: VideoPlatform;

  static getType(): string {
    return "video-embed";
  }

  static clone(node: VideoEmbedNode): VideoEmbedNode {
    return new VideoEmbedNode(
      node.__videoUrl,
      node.__videoId,
      node.__platform,
      node.__key,
    );
  }

  constructor(
    videoUrl: string,
    videoId: string,
    platform: VideoPlatform,
    key?: NodeKey,
  ) {
    super(key);
    this.__videoUrl = videoUrl;
    this.__videoId = videoId;
    this.__platform = platform;
  }

  static importJSON(json: SerializedVideoEmbedNode): VideoEmbedNode {
    return $createVideoEmbedNode(json.videoUrl, json.videoId, json.platform);
  }

  exportJSON(): SerializedVideoEmbedNode {
    return {
      type: "video-embed",
      version: 1,
      videoUrl: this.__videoUrl,
      videoId: this.__videoId,
      platform: this.__platform,
    };
  }

  exportDOM(): DOMExportOutput {
    const src =
      this.__platform === "youtube"
        ? `https://www.youtube.com/embed/${this.__videoId}`
        : `https://player.vimeo.com/video/${this.__videoId}`;

    const container = document.createElement("div");
    container.style.position = "relative";
    container.style.width = "100%";
    container.style.paddingBottom = "56.25%";
    container.style.marginTop = "1rem";
    container.style.marginBottom = "1rem";

    const iframe = document.createElement("iframe");
    iframe.setAttribute("src", src);
    iframe.setAttribute("title", "Video embed");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
    );
    iframe.setAttribute("allowfullscreen", "true");
    iframe.style.position = "absolute";
    iframe.style.top = "0";
    iframe.style.left = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.borderRadius = "0.5rem";

    container.appendChild(iframe);

    return { element: container };
  }

  static importDOM(): DOMConversionMap | null {
    return null;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    return div;
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): ReactElement {
    return (
      <VideoEmbedComponent
        videoId={this.__videoId}
        platform={this.__platform}
        nodeKey={this.__key}
      />
    );
  }

  isInline(): false {
    return false;
  }
}

export function $createVideoEmbedNode(
  videoUrl: string,
  videoId: string,
  platform: VideoPlatform,
): VideoEmbedNode {
  return new VideoEmbedNode(videoUrl, videoId, platform);
}

export function $isVideoEmbedNode(
  node: LexicalNode | null | undefined,
): node is VideoEmbedNode {
  return node instanceof VideoEmbedNode;
}
